import React from 'react';
import { Settings } from '@/Settings';
import { MediaBlock } from '@/declarations/models/blocks/MediaBlock';
import { MediaViewType } from '@/declarations/models/blocks/MediaViewType';
import { isDamsMediaItem, isExternalMediaModel, isM24MediaModel } from '@/utils/typeChecks';
import { ExternalMediaModel } from '@/declarations/models/ExternalMediaModel';
import { BlockPreviewComponent } from '../../lib/declarations/EditorComponentTypes';
import { useCurrentBlock } from '../CurrentBlockProvider';
import ImagePreview from '../../../components/ImagePreview';

export const MediaBlockPreview: BlockPreviewComponent = () => {
  const { block } = useCurrentBlock();
  const currentBlock = block as MediaBlock;

  const getViewClass = () => {
    switch (currentBlock?.view) {
      case MediaViewType.LIST:
        return 'media--auto media--list-common';
      case MediaViewType.LIST_NARROW:
        return 'media--narrow media--list-common';
      case MediaViewType.LIST_WIDE:
        return 'media--wide media--list-common';
      case MediaViewType.LIST_FULL:
        return 'media--full media--list-common';
      case MediaViewType.GRID:
        return 'media--grid';
      case MediaViewType.GRID_NARROW:
        return 'media--grid-narrow';
      case MediaViewType.GRID_WIDE:
        return 'media--grid-wide';
      case MediaViewType.GRID_FULL:
        return 'media--grid-full';
      case MediaViewType.GRID_PACKERY:
        return 'media--autogrid';
      case MediaViewType.GRID_PACKERY_NARROW:
        return 'media--autogrid-narrow';
      case MediaViewType.GRID_PACKERY_WIDE:
        return 'media--autogrid-wide';
      case MediaViewType.GRID_PACKERY_FULL:
        return 'media--autogrid-full';
      case MediaViewType.SLIDESHOW:
        return 'media--slideshow';
      case MediaViewType.SLIDESHOW_NARROW:
        return 'media--slideshow-narrow';
      case MediaViewType.SLIDESHOW_WIDE:
        return 'media--slideshow-wide';
      case MediaViewType.SLIDESHOW_FULL:
        return 'media--slideshow-full';
      case MediaViewType.COMPARE_TWO_IMAGES:
        return 'media--compare';
      case MediaViewType.COMPARE_TWO_IMAGES_NARROW:
        return 'media--compare-narrow';
      case MediaViewType.COMPARE_TWO_IMAGES_WIDE:
        return 'media--compare  width-wide';
      case MediaViewType.COMPARE_TWO_IMAGES_FULL:
        return 'media--compare width-full';
      default:
        return '';
    }
  };

  const getViewWidth = () => {
    if (currentBlock?.view?.includes('wide')) {
      return 'wide';
    }
    if (currentBlock?.view?.includes('full')) {
      return 'full';
    }
    if (currentBlock?.view?.includes('narrow')) {
      return 'narrow';
    }
    if (currentBlock?.view?.includes('article')) {
      return 'article';
    }
    return 'auto';
  };

  const compareImagesArray = () => {
    const arr: string[][] = [[]];

    currentBlock?.items?.forEach((item, i) => {
      const index = Math.floor(i / 2);
      if (!arr[index]) {
        arr[index] = [];
      }
      if (isM24MediaModel(item)) {
        arr[index][i % 2] = item.url;
      }

      if (isExternalMediaModel(item)) {
        const itemWithUrl = item as ExternalMediaModel & { url: string };
        arr[index][i % 2] = itemWithUrl.url;
      }

      if (isDamsMediaItem(item)) {
        const damsEmsDomain = Settings.viteEnvironmentIsProduction ? 'ems.dimu.org' : 'ems-test.dimu.org';
        const baseUrl = item.m24?.ems_id ? `https://${damsEmsDomain}/image/${item.m24.ems_id}` : '';
        arr[index][i % 2] = baseUrl;
      }

      if (arr[index].length === 0) {
        arr.pop();
      }
    });

    if (arr[arr.length - 1]?.length !== 2) {
      arr.pop();
    }

    return arr;
  };

  // Simple slideshow state for preview: show next image (wrap around)
  const [currentSlide, setCurrentSlide] = React.useState(0);

  function handleNextSlide(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    event.preventDefault();
    if (!currentBlock?.items || currentBlock.items.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % currentBlock.items.length);
  }

  function handlePrevSlide(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    event.preventDefault();
    if (!currentBlock?.items || currentBlock.items.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + currentBlock.items.length) % currentBlock.items.length);
  }

  const isWideImages =
    (currentBlock?.view === MediaViewType.LIST_WIDE || currentBlock?.view === MediaViewType.LIST_FULL) &&
    (!currentBlock?.gridSize || currentBlock.gridSize === '1');

  return (
    <>
      <div className={`article__grid alignmodify--${currentBlock?.view}`}>
        <section className='article__bodytext'>
          {currentBlock?.visibletitle && <h2>{currentBlock?.title}</h2>}
          <p style={{ whiteSpace: 'pre-line' }}>{currentBlock?.body}</p>
        </section>
      </div>
      <div className='section__grid'>
        <div
          data-fullscreen={false}
          data-media-thumb-captions-visible={currentBlock.displayCaptionsOnThumbnails || false}
          className={`media media-format--${currentBlock?.format} media-layout--${
            currentBlock?.layout || 'auto'
          } width--${getViewWidth()} ${getViewClass()} gridSize-${currentBlock?.gridSize || 'auto'}`}>
          {[
            MediaViewType.LIST,
            MediaViewType.LIST_NARROW,
            MediaViewType.LIST_WIDE,
            MediaViewType.LIST_FULL,
            MediaViewType.GRID,
            MediaViewType.GRID_NARROW,
            MediaViewType.GRID_WIDE,
            MediaViewType.GRID_FULL,
            MediaViewType.GRID_PACKERY,
            MediaViewType.GRID_PACKERY_NARROW,
            MediaViewType.GRID_PACKERY_WIDE,
            MediaViewType.GRID_PACKERY_FULL,
          ].includes(currentBlock?.view) && (
            <ul className='media__list'>
              {currentBlock?.items?.map((item, index) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <li className={`media__item galleryitem--${item.local?.size || ''}`} key={index}>
                    <figure
                      className={`module module--media type--${item?.type || 'image'}  format--${
                        currentBlock?.format || 'auto'
                      } layout--${currentBlock?.layout || 'auto'}`}>
                      <div className={`module__grid skin-media ${currentBlock?.skin || 'skin-media-auto'}`}>
                        {item.type === 'audio' /* eslint-disable-next-line jsx-a11y/media-has-caption */ && (
                          <audio controls>
                            <source src={item?.play_urls?.mp3} type='audio/mp3' />
                          </audio>
                        )}
                        {item.type === 'video' /* eslint-disable-next-line jsx-a11y/media-has-caption */ && (
                          <video controls poster={item?.content?.poster?.url || item?.url}>
                            <source src={item?.play_urls?.mp4} type='video/mp4' />
                          </video>
                        )}
                        {item.type !== 'audio' && item.type !== 'video' && (
                          <ImagePreview
                            disableFocusPoint
                            mediaObject={item}
                            dimension={isWideImages ? '1440x1440' : ''}
                          />
                        )}
                        {!currentBlock.disableCaptions && (
                          <div className='module__content'>
                            <figcaption className='media__caption'>
                              {!item?.local?.disableCaption && (
                                <span>{item?.local?.caption || item.content?.caption}</span>
                              )}
                              {item?.content?.credits && (
                                <span className='media__credit  caption--credit'>
                                  {item?.content?.credits &&
                                    item?.content?.credits?.length > 0 &&
                                    item?.content?.credits?.map(
                                      (credit) =>
                                        credit?.name && (
                                          <span className={credit?.type} key={credit?.name}>
                                            {credit?.name} &nbsp;
                                          </span>
                                        ),
                                    )}
                                </span>
                              )}
                              {'source' in item && item.source === 'digitaltMuseum' && (
                                <span className='media__credit caption--dm'>digitaltmuseum.org</span>
                              )}
                            </figcaption>
                          </div>
                        )}
                        {!currentBlock.disableZoom && !item?.local?.disableZoom && (
                          <div className='module__action'>
                            <ul className='nav-fullscreen'>
                              <li className='nav-fullscreen-open'>
                                <button
                                  aria-label='Åpne i fullskjerm'
                                  type='button'
                                  className='button  button--fullscreen'>
                                  <i className='button__icon  i-nav-fullscreen' />
                                </button>
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </figure>
                  </li>
                );
              })}
            </ul>
          )}
          {[
            MediaViewType.SLIDESHOW,
            MediaViewType.SLIDESHOW_NARROW,
            MediaViewType.SLIDESHOW_WIDE,
            MediaViewType.SLIDESHOW_FULL,
          ].includes(currentBlock?.view) &&
            currentBlock?.items?.length > 0 && (
              <>
                <div className='media__viewport' style={{ overflow: 'hidden', position: 'relative' }}>
                  <div className='media__nav skin-media'>
                    <ul className='nav-slideshow'>
                      <li className='nav-prev'>
                        <button
                          type='button'
                          onClick={handlePrevSlide}
                          data-action='slideshow-prev'
                          aria-label='Forrige bilde'
                          className='button'>
                          <i className='button__icon i-nav-left' />
                        </button>
                      </li>
                      <li className='nav-next'>
                        <button
                          type='button'
                          onClick={handleNextSlide}
                          data-action='slideshow-next'
                          aria-label='Neste bilde'
                          className='button'>
                          <i className='button__icon i-nav-right' />
                        </button>
                      </li>
                    </ul>
                  </div>
                  <ul className='media__list'>
                    <li className='media__item media_active-slide item--current galleryitem--'>
                      <figure
                        className={`module module--media format--${currentBlock?.format} layout--${currentBlock?.layout} module--small`}>
                        <div
                          className={`module__grid ${
                            isM24MediaModel(currentBlock?.items[currentSlide]) ? 'skin-media' : 'skin-module-media'
                          } ${currentBlock?.skin} `}>
                          <ImagePreview
                            dimension='1440x1440'
                            disableFocusPoint
                            mediaObject={currentBlock?.items[currentSlide]}
                            imgClassName='img-landscape'
                            skin={`${
                              isM24MediaModel(currentBlock?.items[currentSlide]) ? 'skin-media' : 'skin-module-media'
                            } ${currentBlock?.skin || 'skin-media-auto'}`}
                          />
                        </div>
                      </figure>
                    </li>
                  </ul>
                </div>
              </>
            )}
          {[
            MediaViewType.COMPARE_TWO_IMAGES,
            MediaViewType.COMPARE_TWO_IMAGES_NARROW,
            MediaViewType.COMPARE_TWO_IMAGES_WIDE,
            MediaViewType.COMPARE_TWO_IMAGES_FULL,
          ].includes(currentBlock?.view) &&
            currentBlock?.items?.length >= 2 && (
              <>
                {compareImagesArray()?.map((imagePair, index) => {
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <figure key={index} className='module module--media module--normal'>
                      <div className='twentytwenty-wrapper twentytwenty-horizontal'>
                        <div
                          className='module__media twentytwenty-container'
                          style={{
                            height: `480px`,
                          }}>
                          <img
                            src={`${imagePair[0]}?dimension=1200x1200`}
                            alt=''
                            style={{
                              position: 'absolute',
                              overflow: 'hidden',
                              display: 'block',
                              zIndex: 2,
                              clipPath: 'polygon(0% 0%,50% 0%,50% 100%,0% 100%)',
                              top: 0,
                              left: 0,
                            }}
                          />
                          <img
                            src={`${imagePair[1]}?dimension=1200x1200`}
                            alt=''
                            style={{
                              position: 'absolute',
                              overflow: 'hidden',
                              display: 'block',
                              zIndex: 1,
                              right: 0,
                            }}
                          />
                        </div>
                      </div>
                    </figure>
                  );
                })}
              </>
            )}
        </div>
      </div>
    </>
  );
};

export default MediaBlockPreview;
